/**
 * Mixpanel events for languages & shopper-text translation workflows (RGET-3283).
 * Never send translation text, key names, filenames, or shopper PII.
 */
import rmsMixpanelStore from "../../stores/rms-mixpanel-store";

export const TRANSLATION_MIXPANEL_EVENTS = {
	EDIT_STARTED: "d&_translation_edit_started",
	SAVE_COMPLETED: "d&_translation_save_completed",
	LOCALE_CHANGED: "d&_translation_locale_changed",
	CSV_DOWNLOAD_COMPLETED: "d&_translations_csv_download_completed",
	CSV_UPLOAD_COMPLETED: "d&_translations_csv_upload_completed",
	LANGUAGE_CONFIGURATION_COMPLETED: "d&_language_configuration_completed",
	VALIDATION_OPENED: "d&_translation_validation_opened",
	SHOPPER_TEXT_OBJECT_SAVED: "d&_shopper_text_object_saved",
} as const;

/** Fixed surface values from the RGET-3283 analytics contract. */
export enum TranslationSurface {
	LanguagesHub = "languages_hub",
	PortalContent = "portal_content",
	ReturnReasons = "return_reasons",
	ReturnMethods = "return_methods",
	Resolutions = "resolutions",
	Emails = "emails",
	TrackingPortal = "tracking_portal",
	Documents = "documents",
	Storefront = "storefront",
	Policies = "policies",
}

export enum TranslationEntryPoint {
	LanguagesHub = "languages_hub",
	ObjectPage = "object_page",
	Sidebar = "sidebar",
	DeepLink = "deep_link",
	Csv = "csv",
}

export type TranslationEditContext = "existing_object" | "new_object";

export type TranslationResult = "success" | "partial" | "error";

export type LanguageConfigAction = "add" | "enable" | "disable" | "delete" | "set_default";

export type TranslationValidationType = "portal_preview" | "email_preview" | "send_test";

export type ShopperTextObjectType = "return_reason" | "resolution" | "return_method";

/** Scopes that currently use TranslationScope for shopper-facing copy. */
const SCOPE_TO_SURFACE: Record<string, TranslationSurface> = {
	"return-reason": TranslationSurface.ReturnReasons,
	"resolutions": TranslationSurface.Resolutions,
	"return-methods": TranslationSurface.ReturnMethods,
	"packing-slip": TranslationSurface.Documents,
	"address-slip": TranslationSurface.Documents,
	"assistant": TranslationSurface.PortalContent,
	"return-portal-errors": TranslationSurface.PortalContent,
	"tracking-portal": TranslationSurface.TrackingPortal,
	"storefront": TranslationSurface.Storefront,
	"store-front": TranslationSurface.Storefront,
	"policy-rules": TranslationSurface.Policies,
	"return-portal": TranslationSurface.PortalContent,
	"email": TranslationSurface.Emails,
	"tracking-emails": TranslationSurface.Emails,
	"pending-approval-email": TranslationSurface.Emails,
	"returngo-credits-email": TranslationSurface.Emails,
	"manual-credits-email": TranslationSurface.Emails,
	"instant-exchange-reminder-first-email": TranslationSurface.Emails,
	"instant-exchange-reminder-second-email": TranslationSurface.Emails,
	"charged-fees-invoice-email": TranslationSurface.Emails,
	"exchange-order-invoice-email": TranslationSurface.Emails,
};

const editStartedScopes = new Set<string>();

export function mapScopeToSurface(scope: string): TranslationSurface {
	return SCOPE_TO_SURFACE[scope] ?? TranslationSurface.PortalContent;
}

export function resetTranslationEditSession(scope: string): void {
	editStartedScopes.delete(scope);
}

export function trackTranslationEditStarted(props: {
	scope: string;
	locale: string;
	is_default_locale: boolean;
	entry_point?: TranslationEntryPoint;
	edit_context?: TranslationEditContext;
	surface?: TranslationSurface;
}): void {
	if (editStartedScopes.has(props.scope)) {
		return;
	}
	editStartedScopes.add(props.scope);
	rmsMixpanelStore.trackEvent(TRANSLATION_MIXPANEL_EVENTS.EDIT_STARTED, {
		surface: props.surface ?? mapScopeToSurface(props.scope),
		scope: props.scope,
		locale: props.locale,
		is_default_locale: props.is_default_locale,
		entry_point: props.entry_point ?? TranslationEntryPoint.ObjectPage,
		edit_context: props.edit_context ?? "existing_object",
	});
}

export function trackTranslationSaveCompleted(props: {
	scope: string;
	locale: string;
	is_default_locale: boolean;
	result: TranslationResult;
	fields_changed_count: number;
	locales_changed_count: number;
	missing_before: number;
	missing_after: number;
	duration_ms: number;
	entry_point?: TranslationEntryPoint;
	enabled_locale_count?: number;
	surface?: TranslationSurface;
}): void {
	resetTranslationEditSession(props.scope);
	rmsMixpanelStore.trackEvent(TRANSLATION_MIXPANEL_EVENTS.SAVE_COMPLETED, {
		surface: props.surface ?? mapScopeToSurface(props.scope),
		scope: props.scope,
		locale: props.locale,
		is_default_locale: props.is_default_locale,
		entry_point: props.entry_point ?? TranslationEntryPoint.ObjectPage,
		enabled_locale_count: props.enabled_locale_count,
		result: props.result,
		fields_changed_count: props.fields_changed_count,
		locales_changed_count: props.locales_changed_count,
		missing_before: props.missing_before,
		missing_after: props.missing_after,
		duration_ms: props.duration_ms,
	});
}

export function trackTranslationLocaleChanged(props: {
	scope: string;
	from_locale: string;
	to_locale: string;
	has_missing_translation: boolean;
	entry_point?: TranslationEntryPoint;
	surface?: TranslationSurface;
}): void {
	rmsMixpanelStore.trackEvent(TRANSLATION_MIXPANEL_EVENTS.LOCALE_CHANGED, {
		surface: props.surface ?? mapScopeToSurface(props.scope),
		scope: props.scope,
		from_locale: props.from_locale,
		to_locale: props.to_locale,
		entry_point: props.entry_point ?? TranslationEntryPoint.ObjectPage,
		has_missing_translation: props.has_missing_translation,
	});
}

export function trackTranslationsCsvDownloadCompleted(props: {
	enabled_locale_count: number;
	source_page: string;
}): void {
	rmsMixpanelStore.trackEvent(TRANSLATION_MIXPANEL_EVENTS.CSV_DOWNLOAD_COMPLETED, {
		enabled_locale_count: props.enabled_locale_count,
		source_page: props.source_page,
	});
}

export function trackTranslationsCsvUploadCompleted(props: {
	result: TranslationResult;
	locale_count: number;
	changed_rows_count: number;
	missing_before: number;
	missing_after: number;
	duration_ms: number;
}): void {
	rmsMixpanelStore.trackEvent(TRANSLATION_MIXPANEL_EVENTS.CSV_UPLOAD_COMPLETED, {
		result: props.result,
		locale_count: props.locale_count,
		changed_rows_count: props.changed_rows_count,
		missing_before: props.missing_before,
		missing_after: props.missing_after,
		duration_ms: props.duration_ms,
	});
}

export function trackLanguageConfigurationCompleted(props: {
	action: LanguageConfigAction;
	locale: string;
	result: TranslationResult;
	enabled_locale_count: number;
}): void {
	rmsMixpanelStore.trackEvent(TRANSLATION_MIXPANEL_EVENTS.LANGUAGE_CONFIGURATION_COMPLETED, {
		action: props.action,
		locale: props.locale,
		result: props.result,
		enabled_locale_count: props.enabled_locale_count,
	});
}

export function trackTranslationValidationOpened(props: {
	validation_type: TranslationValidationType;
	locale?: string;
	surface: TranslationSurface;
	scope?: string;
	entry_point?: TranslationEntryPoint;
	has_unsaved_changes?: boolean;
}): void {
	rmsMixpanelStore.trackEvent(TRANSLATION_MIXPANEL_EVENTS.VALIDATION_OPENED, {
		validation_type: props.validation_type,
		locale: props.locale,
		surface: props.surface,
		scope: props.scope,
		entry_point: props.entry_point ?? TranslationEntryPoint.ObjectPage,
		has_unsaved_changes: props.has_unsaved_changes ?? false,
	});
}

export function trackShopperTextObjectSaved(props: {
	action: "create" | "update";
	object_type: ShopperTextObjectType;
	enabled_locale_count: number;
	translation_debt_created_count: number;
}): void {
	rmsMixpanelStore.trackEvent(TRANSLATION_MIXPANEL_EVENTS.SHOPPER_TEXT_OBJECT_SAVED, {
		action: props.action,
		object_type: props.object_type,
		enabled_locale_count: props.enabled_locale_count,
		translation_debt_created_count: props.translation_debt_created_count,
	});
}
